/**
 * SCI Field QC — application state.
 *
 * Three jobs:
 *
 *   1. Session and role. `canEdit` is derived from the session role and is the
 *      single switch the UI uses to strip every editing control for a client.
 *      (The matching client-side call guard lives in ./api.ts, and the real
 *      enforcement is server-side.)
 *   2. Who is on shift, persisted to the device, because it stamps every entry
 *      and the crew should not re-pick it every morning.
 *   3. Drafts. `sectionDraft()` keeps a per-section delta in localStorage as
 *      the user types and flushes it on section blur. A failed flush leaves
 *      the delta on the device and raises an unsaved marker; nothing is ever
 *      discarded because a request failed, and re-authenticating after a
 *      session expiry does not touch it.
 *
 * Classic Svelte stores rather than runes: this is a `.ts` file, and `$state`
 * is only available in `.svelte`/`.svelte.ts` modules.
 */

import { derived, writable, type Readable, type Writable } from 'svelte/store';
import { api, isApiError, onUnauthorized, save, setSessionRole } from './api';
import {
  ACCOUNT_AREA_LABELS,
  type ContractReport,
  type DailyReportSummary,
  type EquipmentInspectionSummary,
  type FieldTestLogSummary,
  type ItpItem,
  type JhaSummary,
  type MixDesignSummary,
  type PourFull,
  type PourSummary,
  type PublicProject,
  type Role,
  type Session
} from './types';

/* ==========================================================================
 * Device storage — namespaced, and tolerant of a locked-down browser
 * ======================================================================== */

const NS = 'sci.';

function readLocal<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(NS + key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: unknown): void {
  try {
    localStorage.setItem(NS + key, JSON.stringify(value));
  } catch {
    /* Private mode or a full quota. Losing the draft mirror is survivable;
       throwing here would not be. */
  }
}

function removeLocal(key: string): void {
  try {
    localStorage.removeItem(NS + key);
  } catch {
    /* ignore */
  }
}

/* ==========================================================================
 * Session
 * ======================================================================== */

export type BootState = 'loading' | 'ready' | 'error';

export const bootState = writable<BootState>('loading');
export const session = writable<Session | null>(null);
export const project = writable<PublicProject | null>(null);
export const viaShareToken = writable(false);

export const role: Readable<Role | null> = derived(session, ($session) => $session?.role ?? null);

/** True only for an SCI crew session. */
export const isCrew: Readable<boolean> = derived(role, ($role) => $role === 'crew');

/**
 * The one switch every screen reads. A client session renders values as text
 * and issues no mutating call at all.
 */
export const canEdit: Readable<boolean> = isCrew;

/**
 * True for the mix design office session.
 *
 * Note that `canEdit` is deliberately NOT true for an office session: it is
 * the field QC record's write switch, and the office login has no business
 * writing one. The mix design screens gate on `isOffice` instead.
 */
export const isOffice: Readable<boolean> = derived(role, ($role) => $role === 'office');

/** True only for an admin session. The five quality cards show their Delete
 *  to admin alone — the DELETE routes refuse a quality login, so a button
 *  shown to one would be a button that cannot work. */
export const isAdmin: Readable<boolean> = derived(
  session,
  ($session) => $session?.area === 'admin'
);

/**
 * Human label for the current office session's area (e.g. 'Mix designs'),
 * for the app bar title. Null for crew, client, or signed out.
 */
export const accountAreaLabel: Readable<string | null> = derived(
  session,
  ($session) => ($session?.area ? ACCOUNT_AREA_LABELS[$session.area] : null)
);

/** Who is on shift, as the server has it. */
export const shiftName: Readable<string | null> = derived(
  session,
  ($session) => $session?.shiftName ?? null
);

/** True once we have a session and a shift name — i.e. ready to record. */
export const ready: Readable<boolean> = derived(
  [session, bootState],
  ([$session, $boot]) => $boot === 'ready' && $session !== null && !!$session.shiftName
);

// Keep the API client's role guard in step with the session.
session.subscribe(($session) => setSessionRole($session?.role ?? null, $session?.area ?? null));

/* ==========================================================================
 * Shift names remembered on this device
 * ======================================================================== */

const SHIFT_NAMES_KEY = 'shift.names';
const SHIFT_LAST_KEY = 'shift.last';

/** Names previously used on this device, most recent first. */
export const knownShiftNames = writable<string[]>(readLocal<string[]>(SHIFT_NAMES_KEY) ?? []);

knownShiftNames.subscribe((names) => writeLocal(SHIFT_NAMES_KEY, names));

/** The name last used on this device, offered as the default on the picker. */
export function lastShiftName(): string | null {
  return readLocal<string>(SHIFT_LAST_KEY);
}

function rememberShiftName(name: string): void {
  const trimmed = name.trim();
  if (!trimmed) return;
  writeLocal(SHIFT_LAST_KEY, trimmed);
  knownShiftNames.update((names) => {
    const next = names.filter((item) => item.toLowerCase() !== trimmed.toLowerCase());
    next.unshift(trimmed);
    return next.slice(0, 12);
  });
}

/** Forget a name from the picker list (someone left the crew). */
export function forgetShiftName(name: string): void {
  knownShiftNames.update((names) => names.filter((item) => item !== name));
}

/* ==========================================================================
 * Project code remembered on this device
 * ======================================================================== */

// Names kept as-is (`PROJECT_CODE_KEY`, `rememberProjectCode`) even though the
// field now accepts an account login too: renaming the storage key would
// forget what people have already typed on their devices.
const PROJECT_CODE_KEY = 'login.code';

/** The login last used here (project code or account login) — a device lives on one job. */
export function rememberedProjectCode(): string {
  return readLocal<string>(PROJECT_CODE_KEY) ?? '';
}

export function rememberProjectCode(code: string): void {
  const trimmed = code.trim();
  if (trimmed) writeLocal(PROJECT_CODE_KEY, trimmed);
}

/* ==========================================================================
 * Notices — banners, because the spec forbids modal dialogs
 * ======================================================================== */

export type NoticeTone = 'info' | 'ok' | 'warn' | 'error';

export interface NoticeAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface Notice {
  id: number;
  tone: NoticeTone;
  title: string;
  detail?: string;
  actions?: NoticeAction[];
  /** Sticky notices stay until dismissed; the rest fade after 6 s. */
  sticky?: boolean;
}

let noticeSeq = 0;
export const notices = writable<Notice[]>([]);

export function notify(notice: Omit<Notice, 'id'>): number {
  const id = ++noticeSeq;
  notices.update((list) => [...list, { ...notice, id }]);
  if (!notice.sticky) {
    setTimeout(() => dismissNotice(id), 6000);
  }
  return id;
}

export function dismissNotice(id: number): void {
  notices.update((list) => list.filter((item) => item.id !== id));
}

export function clearNotices(): void {
  notices.set([]);
}

/** Turn any thrown value into a banner. Never a dialog. */
export function notifyError(error: unknown, title = 'Something went wrong'): void {
  const message = isApiError(error)
    ? error.message
    : error instanceof Error
      ? error.message
      : String(error);
  notify({ tone: 'error', title, detail: message, sticky: true });
}

/* ==========================================================================
 * Session lifecycle
 * ======================================================================== */

/** `project` is null for an office session — the mix design library is company-wide. */
function applyLogin(payload: { session: Session; project: PublicProject | null }): void {
  session.set(payload.session);
  project.set(payload.project);
  if (payload.session.shiftName) rememberShiftName(payload.session.shiftName);
}

/** Called once at start-up. Never throws; sets bootState instead. */
export async function bootstrap(): Promise<void> {
  bootState.set('loading');
  try {
    const response = await api.getSession();
    session.set(response.session);
    project.set(response.project);
    viaShareToken.set(response.via_share_token);
    bootState.set('ready');
  } catch (error) {
    bootState.set('error');
    notifyError(error, 'Could not reach the server');
  }
}

/**
 * The one login. `code` resolves to a project code (crew or client session)
 * or an account login (office session) on the server; the caller never says
 * which it means.
 */
export async function login(
  code: string,
  password: string,
  shift?: string | null
): Promise<void> {
  const response = await api.login({ code, password, shift_name: shift ?? null });
  applyLogin(response);
  viaShareToken.set(false);
}

export async function shareLogin(code: string, token: string): Promise<void> {
  const response = await api.shareLogin({ code, token });
  applyLogin(response);
  viaShareToken.set(true);
}

/** Shift picker. Also remembers the name on this device. */
export async function chooseShift(name: string | null): Promise<void> {
  const response = await api.setShift({ shift_name: name });
  applyLogin(response);
  if (name) rememberShiftName(name);
}

export async function logout(): Promise<void> {
  try {
    await api.logout();
  } finally {
    session.set(null);
    project.set(null);
    viaShareToken.set(false);
    pours.set([]);
    currentPour.set(null);
    mixDesigns.set([]);
    jhaRecords.set([]);
    fieldTestLogs.set([]);
    equipmentInspections.set([]);
    dailyReports.set([]);
    contractReports.set([]);
  }
}

/**
 * A 401 mid-session drops us to the login screen but leaves every draft on the
 * device, so re-authenticating resumes exactly where the user was.
 */
onUnauthorized(() => {
  session.set(null);
  notify({
    tone: 'warn',
    title: 'Signed out',
    detail: 'Your session expired. Sign in again — your part-filled record is still here.',
    sticky: true
  });
});

/* ==========================================================================
 * Pours
 * ======================================================================== */

export const pours = writable<PourSummary[]>([]);
export const poursLoading = writable(false);
export const currentPour = writable<PourFull | null>(null);
export const currentPourLoading = writable(false);

export async function loadPours(): Promise<void> {
  poursLoading.set(true);
  try {
    const response = await api.listPours();
    pours.set(response.pours);
  } catch (error) {
    notifyError(error, 'Could not load the pour list');
  } finally {
    poursLoading.set(false);
  }
}

export async function loadPour(pourId: number): Promise<PourFull | null> {
  currentPourLoading.set(true);
  try {
    const response = await api.getPour(pourId);
    currentPour.set(response.pour);
    return response.pour;
  } catch (error) {
    notifyError(error, 'Could not load the pour record');
    return null;
  } finally {
    currentPourLoading.set(false);
  }
}

/** Merge a server-canonical slice back into the pour held in memory. */
export function mergeCurrentPour(patch: Partial<PourFull>): void {
  currentPour.update((pour) => (pour ? { ...pour, ...patch } : pour));
}

/** Replace one ITP item in place after a successful patch. */
export function replaceItpItem(item: ItpItem): void {
  currentPour.update((pour) =>
    pour
      ? { ...pour, itp_items: pour.itp_items.map((row) => (row.id === item.id ? item : row)) }
      : pour
  );
}

/** Does this ITP / job-setup item carry a Hold point? */
export function isHoldPoint(pointType: string): boolean {
  return pointType.toUpperCase().includes('H');
}

/** Hold points currently failed — these block closing the pour. */
export function failedHoldPoints(items: ItpItem[]): ItpItem[] {
  return items.filter((item) => item.status === 'failed' && isHoldPoint(item.point_type));
}

/**
 * The blocking prompt the spec requires when a Hold point fails. A banner, not
 * a dialog: production stopping is urgent, but trapping the user in a modal
 * mid-pour is exactly what the spec forbids.
 */
export function raiseHoldPointAlarm(item: ItpItem): void {
  notify({
    tone: 'error',
    sticky: true,
    title: `HOLD POINT — item ${item.item_no} failed`,
    detail:
      `${item.title}. Production is to STOP and Kiewit QC is to be notified before restart. ` +
      'Record a stop-hold event with the corrective action taken.'
  });
}

/* ==========================================================================
 * Drafts — auto-saved to the device as the user types
 * ======================================================================== */

interface DraftEnvelope<T> {
  v: 1;
  at: string;
  /** Only the fields the user actually changed. */
  delta: Partial<T>;
}

const DRAFT_PREFIX = 'draft.';
const DRAFT_DEBOUNCE_MS = 200;

export interface SectionDraftOptions<T extends Record<string, unknown>> {
  /** Stable and unique, e.g. `pour:7:header`. Becomes the storage key. */
  key: string;
  /** Shown in the unsaved bar, e.g. 'Pour header'. */
  label: string;
  /** The server's current values for this section. */
  base: T;
  /**
   * Flush. Receives ONLY the changed fields, which is what the contract's
   * section patches expect. Throwing marks the section unsaved.
   */
  save: (changed: Partial<T>, full: T) => Promise<unknown>;
}

export interface SectionDraft<T extends Record<string, unknown>> extends Readable<T> {
  readonly key: string;
  readonly label: string;
  /** True if a draft was found on the device and restored at construction. */
  readonly restored: boolean;
  /** Set one field. Persists to the device immediately (debounced). */
  setField<K extends keyof T>(field: K, value: T[K]): void;
  /** Set several fields at once. */
  patch(partial: Partial<T>): void;
  /** Fields differing from the server values. */
  changes(): Partial<T>;
  /** True while there are unflushed changes. */
  dirty: Readable<boolean>;
  /** Push to the server. Resolves true on success, false if it was retained. */
  flush(): Promise<boolean>;
  /** Adopt new server values, keeping any still-unflushed edits on top. */
  rebase(base: T): void;
  /** Throw the local edits away and go back to the server values. */
  discard(): void;
}

/**
 * Build a draft for one editable section.
 *
 * The delta — not the whole record — is what lives on the device, so a stale
 * draft can never clobber a field the user did not touch, even if another QC
 * person changed the same record from another phone.
 */
export function sectionDraft<T extends Record<string, unknown>>(
  options: SectionDraftOptions<T>
): SectionDraft<T> {
  const storageKey = DRAFT_PREFIX + options.key;
  const stored = readLocal<DraftEnvelope<T>>(storageKey);
  const restored = stored !== null && Object.keys(stored.delta ?? {}).length > 0;

  let base: T = options.base;
  let delta: Partial<T> = restored ? { ...(stored as DraftEnvelope<T>).delta } : {};

  const value: Writable<T> = writable({ ...base, ...delta });
  const dirtyStore = writable(restored);

  let writeTimer: ReturnType<typeof setTimeout> | undefined;

  function persist(): void {
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(() => {
      if (Object.keys(delta).length === 0) removeLocal(storageKey);
      else writeLocal(storageKey, { v: 1, at: new Date().toISOString(), delta });
    }, DRAFT_DEBOUNCE_MS);
  }

  function republish(): void {
    value.set({ ...base, ...delta });
    dirtyStore.set(Object.keys(delta).length > 0);
  }

  function patch(partial: Partial<T>): void {
    const writable_ = delta as Record<PropertyKey, unknown>;
    for (const [field, next] of Object.entries(partial) as [keyof T, T[keyof T]][]) {
      // A value equal to the server's is not a change: dropping it keeps the
      // delta honest, so an untouched field can never be re-sent later.
      if (Object.is(base[field], next)) delete delta[field];
      else writable_[field as PropertyKey] = next;
    }
    republish();
    persist();
  }

  return {
    key: options.key,
    label: options.label,
    restored,
    subscribe: value.subscribe,
    dirty: { subscribe: dirtyStore.subscribe },

    setField(field, next) {
      patch({ [field]: next } as unknown as Partial<T>);
    },

    patch,

    changes() {
      return { ...delta };
    },

    async flush() {
      const changed = { ...delta };
      if (Object.keys(changed).length === 0) return true;
      const full = { ...base, ...delta };
      // The `true` sentinel keeps `null` meaning "failed" even when the caller's
      // save resolves to nothing.
      const result = await save(options.key, options.label, async () => {
        await options.save(changed, full);
        return true as const;
      });
      if (result === null) return false; // draft retained; unsaved marker raised

      // Adopt what we just sent; `rebase` confirms it when the caller feeds the
      // server's canonical row back in.
      base = full;

      // Retire ONLY the fields that were actually sent. A field the user edited
      // while this request was in flight is still newer than the server and
      // must stay in the delta — clearing the whole delta here would silently
      // drop everything typed during a slow save.
      for (const field of Object.keys(changed) as (keyof T)[]) {
        if (Object.is(delta[field], changed[field])) delete delta[field];
      }
      republish();
      persist();
      return true;
    },

    rebase(nextBase) {
      base = nextBase;
      for (const field of Object.keys(delta) as (keyof T)[]) {
        if (Object.is(base[field], delta[field])) delete delta[field];
      }
      republish();
      persist();
    },

    discard() {
      delta = {};
      removeLocal(storageKey);
      republish();
    }
  };
}

/** Wipe every draft on this device. Only used when signing out deliberately. */
export function clearAllDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(NS + DRAFT_PREFIX)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** How many sections on this device hold unflushed edits. */
export function deviceDraftCount(): number {
  try {
    let count = 0;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(NS + DRAFT_PREFIX)) count += 1;
    }
    return count;
  } catch {
    return 0;
  }
}

/* ==========================================================================
 * Small helpers shared by screens
 * ======================================================================== */

/** '' and null both mean "not entered"; anything else parses as a number. */
export function toNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Render a nullable value for an input's `value`. */
export function toInput(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** '' -> null, so a cleared text field clears the column. */
export function toText(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/** Today's date as 'YYYY-MM-DD' in the device's own timezone. */
export function todayIso(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/** Short local time for stamps in lists. */
export function formatTime(value: string | null): string {
  if (!value) return '';
  if (/^\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Read the pour list's completion state as a single sentence. */
export function pourOutstanding(pour: PourSummary): string {
  const parts: string[] = [];
  const itpLeft = pour.itp_total_count - pour.itp_recorded_count;
  if (itpLeft > 0) parts.push(`${itpLeft} ITP`);
  const endLeft = pour.end_of_pour_total_count - pour.end_of_pour_recorded_count;
  if (endLeft > 0) parts.push(`${endLeft} end-of-pour`);
  if (pour.chute_test_count === 0) parts.push('no chute tests');
  return parts.length === 0 ? 'Complete' : `${parts.join(', ')} outstanding`;
}

/* ==========================================================================
 * Mix designs
 * ======================================================================== */

export const mixDesigns = writable<MixDesignSummary[]>([]);
export const mixDesignsLoading = writable(false);

export async function loadMixDesigns(): Promise<void> {
  mixDesignsLoading.set(true);
  try {
    const response = await api.listMixDesigns();
    mixDesigns.set(response.mix_designs);
  } catch (error) {
    notifyError(error, 'Could not load the mix designs');
  } finally {
    mixDesignsLoading.set(false);
  }
}

/* ==========================================================================
 * Quality area — JHA, Concrete Field Testing, Equipment Safety Inspection,
 * Daily Report. List state only; each form's own record state is Task 5/6's
 * job.
 * ======================================================================== */

export const jhaRecords = writable<JhaSummary[]>([]);
export const jhaRecordsLoading = writable(false);

export async function loadJhaRecords(siteId?: number | null): Promise<void> {
  jhaRecordsLoading.set(true);
  try {
    const response = await api.listJha(siteId);
    jhaRecords.set(response.jha);
  } catch (error) {
    notifyError(error, 'Could not load the JHAs');
  } finally {
    jhaRecordsLoading.set(false);
  }
}

export const fieldTestLogs = writable<FieldTestLogSummary[]>([]);
export const fieldTestLogsLoading = writable(false);

export async function loadFieldTestLogs(siteId?: number | null): Promise<void> {
  fieldTestLogsLoading.set(true);
  try {
    const response = await api.listFieldTests(siteId);
    fieldTestLogs.set(response.field_test_logs);
  } catch (error) {
    notifyError(error, 'Could not load the field test logs');
  } finally {
    fieldTestLogsLoading.set(false);
  }
}

export const equipmentInspections = writable<EquipmentInspectionSummary[]>([]);
export const equipmentInspectionsLoading = writable(false);

export async function loadEquipmentInspections(siteId?: number | null): Promise<void> {
  equipmentInspectionsLoading.set(true);
  try {
    const response = await api.listEquipment(siteId);
    equipmentInspections.set(response.equipment_inspections);
  } catch (error) {
    notifyError(error, 'Could not load the equipment inspections');
  } finally {
    equipmentInspectionsLoading.set(false);
  }
}

export const dailyReports = writable<DailyReportSummary[]>([]);
export const dailyReportsLoading = writable(false);

export async function loadDailyReports(siteId?: number | null): Promise<void> {
  dailyReportsLoading.set(true);
  try {
    const response = await api.listDailyReports(siteId);
    dailyReports.set(response.daily_reports);
  } catch (error) {
    notifyError(error, 'Could not load the daily reports');
  } finally {
    dailyReportsLoading.set(false);
  }
}

export const contractReports = writable<ContractReport[]>([]);
export const contractReportsLoading = writable(false);

export async function loadContractReports(siteId?: number | null): Promise<void> {
  contractReportsLoading.set(true);
  try {
    const response = await api.listContractReports(siteId);
    contractReports.set(response.contract_reports);
  } catch (error) {
    notifyError(error, 'Could not load the contract reports');
  } finally {
    contractReportsLoading.set(false);
  }
}
